import { NOTIFICATION_HISTORY_WINDOW_DAYS } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { t } from '../../i18n';
import { groupDeliveriesByTier, useNotificationRecent } from '../../lib/notification-recent';
import { usePagedQuery } from '../../lib/paged';
import { EmptyState } from '../EmptyState';
import { LoadMore } from '../LoadMore';
import { Panel } from '../Panel';
import { QueryBoundary } from '../QueryBoundary';
import { Skeleton } from '../Skeleton';
import { NotificationRecentList } from './NotificationRecentList';

// The recent list's CONTAINER half (notifications overhaul, slice 21): one
// keyset-paged read over `GET /v1/me/notification-deliveries`, mounted after
// Advanced — the page's own record of what it emailed, so the switches above
// can be read against what they did. The presenter next door owns the layout.
//
// The panel's subtitle and its empty line both name the window through the
// domain's constant, so the day the server widens the window the copy moves
// with it — the number in prose can never disagree with the rows behind it.

export function NotificationRecent(props: { token: string | null }): JSX.Element {
  const list = usePagedQuery((cursor) => useNotificationRecent(cursor, props.token));

  return (
    <Panel title={t('notifications_settings.recent.title')} class="notification-recent-panel">
      <p class="notification-recent__subtitle">
        {t('notifications_settings.recent.subtitle', { days: NOTIFICATION_HISTORY_WINDOW_DAYS })}
      </p>
      <QueryBoundary
        query={list.view}
        loading={<Skeleton height="10rem" />}
        // Emptiness is decided on the SHOWN rows: the presenter drops
        // hidden-tier rows, so a page of only hidden rows is empty here —
        // unless a next cursor promises more, in which case the Load more
        // under the list is the honest next step, not the empty sentence.
        isEmpty={(rows) => groupDeliveriesByTier(rows).length === 0 && !list.hasMore()}
        empty={
          <EmptyState
            message={t('notifications_settings.recent.empty', {
              days: NOTIFICATION_HISTORY_WINDOW_DAYS,
            })}
          />
        }
      >
        {(rows) => (
          <>
            <NotificationRecentList rows={rows} />
            <LoadMore list={list} />
          </>
        )}
      </QueryBoundary>
    </Panel>
  );
}
